import { createContext, useContext, useState, useEffect } from 'react';

const SettingsContext = createContext();

export const useSettings = () => {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
};

export const SettingsProvider = ({ children }) => {
  const [settings, setSettings] = useState({
    backgroundColor: '#080b14',
    backgroundImage: '',
    backgroundOpacity: 1,
    iconSize: 'medium', // small, medium, large
    iconOpacity: 1,
    layoutMode: 'standard', // standard, compact, spacious
    widgetDisplay: 'normal', // normal, fullsize, compact
    cardBorderRadius: 'default', // subtle, default, rounded
    animationsEnabled: true,
    fontSize: 'normal' // small, normal, large
  });

  useEffect(() => {
    const saved = localStorage.getItem('dashboardSettings');
    if (saved) {
      try {
        setSettings(JSON.parse(saved));
      } catch (err) {
        console.error('Failed to load settings:', err);
      }
    }
  }, []);

  const updateSettings = (newSettings) => {
    const updated = { ...settings, ...newSettings };
    setSettings(updated);
    localStorage.setItem('dashboardSettings', JSON.stringify(updated));
    applySettingsToDOM(updated);
  };

  const applySettingsToDOM = (sett) => {
    const root = document.documentElement;

    // Icon sizes
    const iconSizeMap = {
      small: '16px',
      medium: '20px',
      large: '28px'
    };
    root.style.setProperty('--icon-size', iconSizeMap[sett.iconSize] || '20px');
    root.style.setProperty('--icon-opacity', sett.iconOpacity);

    // Border radius
    const borderMap = {
      subtle: '8px',
      default: '16px',
      rounded: '24px'
    };
    root.style.setProperty('--border-radius', borderMap[sett.cardBorderRadius] || '16px');

    // Font size
    const fontMap = {
      small: '14px',
      normal: '16px',
      large: '18px'
    };
    root.style.setProperty('--base-font-size', fontMap[sett.fontSize] || '16px');

    // Layout spacing
    const layoutMap = {
      compact: '8px',
      standard: '16px',
      spacious: '24px'
    };
    root.style.setProperty('--layout-spacing', layoutMap[sett.layoutMode] || '16px');

    // Background
    const bgElement = document.documentElement;
    if (sett.backgroundImage) {
      bgElement.style.backgroundImage = `url(${sett.backgroundImage})`;
      bgElement.style.backgroundSize = 'cover';
      bgElement.style.backgroundAttachment = 'fixed';
    } else {
      bgElement.style.backgroundColor = sett.backgroundColor;
      bgElement.style.backgroundImage = 'none';
    }
    bgElement.style.setProperty('--bg-opacity', sett.backgroundOpacity);

    // Animations
    if (!sett.animationsEnabled) {
      document.documentElement.style.setProperty('--animation-duration', '0s');
    } else {
      document.documentElement.style.setProperty('--animation-duration', '0.3s');
    }
  };

  useEffect(() => {
    applySettingsToDOM(settings);
  }, [settings]);

  return (
    <SettingsContext.Provider value={{ settings, updateSettings }}>
      {children}
    </SettingsContext.Provider>
  );
};
